import React from 'react';
import { useLocation } from 'react-router-dom';
import ProductPage from '../components/seo/ProductPage';
import { isEnglishPath } from '../utils/i18nRoutes';

const ALTERNATES = [
  { hreflang: 'es', href: '/exoesqueleto' },
  { hreflang: 'en', href: '/en/exoesqueleto' },
  { hreflang: 'x-default', href: '/exoesqueleto' },
];

const CONTENT = {
  es: {
    seoTitle: 'Exoesqueleto | Menos carga, mismo turno | Valoriza',
    seoDescription: 'Dispositivo de asistencia mecánica que reduce la carga física en barrido, carga y descarga. Probado en turno completo en recogida real.',
    breadcrumbLabel: 'Exoesqueleto',
    tag: 'Asistencia ergonómica',
    titleMain: 'Exoesqueleto: menos carga, mismo',
    titleAccent: 'turno',
    lead: 'Dispositivo de asistencia mecánica que reduce la carga física en barrido, carga y descarga. Probado en turno completo en recogida real.',
    problema: 'Las tareas repetitivas y de esfuerzo físico intenso, como las de recogida de residuos o barrido urbano, generan una alta carga ergonómica en los operarios. Esto puede derivar en lesiones musculoesqueléticas a medio y largo plazo, afectando la salud del trabajador y la eficiencia operativa.',
    comoFunciona: 'El exoesqueleto es un dispositivo de asistencia mecánica que se acopla al cuerpo del operario. Proporciona soporte y reduce la carga física en tareas de barrido, carga y descarga manual. Ha sido probado en turnos completos de recogida de residuos, demostrando su eficacia en la disminución de la fatiga y la prevención de lesiones.',
    paraQuien: 'Operarios de servicios urbanos, recogida de residuos, limpieza viaria y mantenimiento. Empresas que buscan mejorar la ergonomía de sus trabajadores y prevenir bajas laborales por sobreesfuerzo físico.',
    enlaces: [{ label: 'Robótica de campo: más allá de la asistencia', to: '/robotica' }, { label: 'Retos: reducción del riesgo físico', to: '/retos' }],
    ctaText: '¿Tienes un reto parecido?',
  },
  en: {
    seoTitle: 'Exoskeleton | Less load, same shift | Valoriza',
    seoDescription: 'Mechanical assistance device that reduces physical load during sweeping, loading and unloading. Tested for a full shift in real collection operations.',
    breadcrumbLabel: 'Exoskeleton',
    tag: 'Ergonomic assistance',
    titleMain: 'Exoskeleton: less load, same',
    titleAccent: 'shift',
    lead: 'Mechanical assistance device that reduces physical load during sweeping, loading and unloading. Tested for a full shift in real collection operations.',
    problema: 'Repetitive tasks involving intense physical effort, such as waste collection or street sweeping, place a heavy ergonomic load on workers. This can lead to musculoskeletal injuries over the medium and long term, affecting both worker health and operational efficiency.',
    comoFunciona: 'The exoskeleton is a mechanical assistance device worn on the operator\u2019s body. It provides support and reduces physical load during sweeping, loading and manual unloading tasks. It has been tested over full waste-collection shifts, proving effective at reducing fatigue and preventing injury.',
    paraQuien: 'Urban services, waste collection, street cleaning and maintenance operators. Companies looking to improve worker ergonomics and prevent sick leave caused by physical overexertion.',
    enlaces: [{ label: 'Field robotics: beyond assistance', to: '/en/robotica' }, { label: 'Challenges: reducing physical risk', to: '/en/challenges' }],
    ctaText: 'Do you have a similar challenge?',
  },
};

export default function ExoesqueletoPage() {
  const { pathname } = useLocation();
  const locale = isEnglishPath(pathname) ? 'en' : 'es';
  const c = CONTENT[locale];
  const path = locale === 'en' ? '/en/exoesqueleto' : '/exoesqueleto';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Exoesqueleto',
    description: c.seoDescription,
    url: `https://innovation.valorizasm.com${path}`,
    brand: { '@id': 'https://innovation.valorizasm.com/#organization' },
  };

  return (
    <ProductPage
      testId="exoesqueleto-page"
      path={path}
      locale={locale}
      alternates={ALTERNATES}
      jsonLd={[jsonLd]}
      problemaLabel={locale === 'en' ? 'The problem' : 'El problema'}
      comoFuncionaLabel={locale === 'en' ? 'How it works' : 'Cómo funciona'}
      cifrasLabel={locale === 'en' ? 'Figures' : 'Cifras'}
      paraQuienLabel={locale === 'en' ? "Who it's for" : 'Para quién'}
      enlacesLabel={locale === 'en' ? 'Links' : 'Enlaces'}
      {...c}
    />
  );
}
